import React from "react";
import { Box, Typography, LinearProgress, Fade, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Sleek Progressive Analysis Tracker
 * Shows only current active step below progress bar
 */
const ProgressiveAnalysisTracker = ({ steps, overallProgress, currentStepNumber }) => {
  // Use the provided overallProgress if available, otherwise calculate from completed steps
  const totalSteps = steps.length || 1;
  const completedSteps = steps.filter(
    (step) => step.status === "success" || step.status === "completed"
  ).length;
  const calculatedProgress = (completedSteps / totalSteps) * 100;

  // Prefer overallProgress from parent if provided, otherwise use calculated
  const displayProgress =
    overallProgress !== undefined && overallProgress !== null
      ? overallProgress
      : calculatedProgress;

  // Get current step by number (1-indexed) or fall back to first in-progress/completed
  // For parallel execution, prefer showing the most recently active step if provided
  let currentStep;

  if (currentStepNumber && steps[currentStepNumber - 1]) {
    // Use the explicitly provided step number
    currentStep = steps[currentStepNumber - 1];
  } else {
    // Fallback: Find first in-progress step, or last completed, or first pending
    currentStep =
      steps.find((step) => step.status === "in-progress") ||
      steps.filter((step) => step.status === "completed").pop() ||
      steps.find((step) => step.status === "pending") ||
      steps[0];
  }

  const isComplete = displayProgress >= 100;

  // Debug logging
  React.useEffect(() => {
    console.log(`🎯 ProgressiveAnalysisTracker - Current Step #${currentStepNumber}:`, {
      label: currentStep?.label,
      status: currentStep?.status,
      id: currentStep?.id,
      completedSteps,
      totalSteps,
      calculatedProgress,
      overallProgress,
      displayProgress,
    });
  }, [currentStepNumber, currentStep?.label, completedSteps, overallProgress, displayProgress]);

  return (
    <Box
      sx={{
        width: 500,
        margin: "auto",
        bgcolor: "background.paper",
        borderRadius: 4,
        p: 4,
        boxShadow: 24,
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
            {isComplete ? "Analysis Complete!" : "Preparing Cost Estimate..."}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
            {Math.round(displayProgress)}% {/*{completedSteps}/{totalSteps} */}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={displayProgress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "action.disabledBackground",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              bgcolor: isComplete ? "success.main" : "primary.main",
              transition: "transform 0.4s ease-in-out, background-color 0.3s ease",
            },
          }}
        />
      </Box>

      {/* Current Step Message (Grey Text Only) */}
      <Fade in={true} timeout={500} key={currentStep?.id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 2,
            px: 3,
          }}
        >
          {isComplete ? (
            <>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 24 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "success.main",
                  fontStyle: "italic",
                }}
              >
                Analysis Complete!
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={20} sx={{ color: "text.disabled" }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                {currentStep?.label || "Initializing..."}
              </Typography>
            </>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default ProgressiveAnalysisTracker;
